import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Check, Gift, Send, Sparkles, ArrowUpRight, Wifi } from "lucide-react";
import { Button } from "@/components/ui/button";

const CONTACT_EMAIL = "cafes@medinafidelite.ma";

const fidelityOffer = {
  name: "Programme de fidélité",
  priceMain: "0",
  priceUnit: "DH",
  priceSuffix: "/ mois",
  priceNote: "Gratuit, sans limite de clients, pour toujours.",
  description:
    "Tout le programme de fidélité numérique, offert à votre commerce.",
  features: [
    "Carte de fidélité numérique illimitée",
    "Clients actifs illimités",
    "Tableau de bord clientèle complet",
    "Capture par QR et portail Wi-Fi",
    "Conformité loi 09-08 et CNDP gérée",
    "Installation et support inclus",
  ],
  cta: "Créer mon programme",
  mailSubject: "Inscription - Programme de fidélité gratuit",
};

// Tarifs fixés le 2026-07-03 (remplacent la base 3× Twilio, jugée trop chère) :
// base de coût WhatsApp « Rest of Africa » (~$0.02–0.05/message) / Bird SMS,
// vendu en dégressif 2,00 → 1,50 DH/SMS selon la taille du pack.
const campaignOffer = {
  name: "Campagnes marketing",
  priceMain: "1,50",
  priceUnit: "DH",
  priceSuffix: "/ SMS",
  priceLeadIn: "À partir de",
  priceNote:
    "Packs de crédits prépayés, tarif dégressif. Pas d'abonnement, pas de minimum mensuel.",
  description:
    "Le seul service payant : les campagnes SMS qui font revenir vos clients.",
  packs: [
    { sms: 100, priceDh: 200, unitDh: "2,00" },
    { sms: 500, priceDh: 900, unitDh: "1,80" },
    { sms: 1000, priceDh: 1650, unitDh: "1,65" },
    { sms: 2500, priceDh: 3750, unitDh: "1,50" },
  ],
  features: [
    "SMS ciblés par segment : fréquence, dernière visite, récompenses",
    "Envoi uniquement aux clients ayant consenti",
    "Heures de silence et désinscription gérées automatiquement",
    "Rapports d'envoi détaillés",
  ],
  cta: "Commander un pack",
  mailSubject: "Commande - Pack SMS",
};

const frNumber = new Intl.NumberFormat("fr-FR");

const cardVariants = {
  hidden: { opacity: 0, y: 30, filter: "blur(8px)" },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: {
      delay: i * 0.12,
      duration: 0.6,
      ease: [0.25, 0.46, 0.45, 0.94] as const,
    },
  }),
};

export const Pricing = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.15 });

  return (
    <section id="tarifs" ref={ref} className="relative py-20 lg:py-28 overflow-hidden">
      <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-primary/[0.03] rounded-full blur-[120px] translate-x-1/3 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-accent/[0.04] rounded-full blur-[100px] -translate-x-1/3 pointer-events-none" />

      <div className="container relative mx-auto px-4 lg:px-6 max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-12"
        >
          <div className="inline-flex items-center gap-2 bg-primary/8 text-primary border border-primary/15 rounded-full px-4 py-1.5 text-sm font-medium mb-6">
            Le programme de fidélité est 100 % gratuit
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground tracking-tight mb-4">
            Le programme est gratuit,{" "}
            <span className="text-gradient">payez seulement vos campagnes</span>
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Carte de fidélité, tableau de bord, conformité : tout est inclus, sans
            abonnement, pour un nombre illimité de clients. Votre seul coût : les
            campagnes SMS que vous décidez d&apos;envoyer.
          </p>
        </motion.div>

        {/* Free program + pay-per-use campaigns */}
        <div className="grid md:grid-cols-2 gap-5 lg:gap-6 items-stretch max-w-4xl mx-auto">
          {/* Fidelity program — free */}
          <motion.div
            custom={1}
            variants={cardVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="relative flex flex-col rounded-2xl p-7 lg:p-8 border border-border/60 bg-card transition-all hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5"
          >
            <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary shadow-sm">
              <Gift className="h-6 w-6" />
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-bold text-foreground mb-1">{fidelityOffer.name}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed min-h-[2.5rem]">
                {fidelityOffer.description}
              </p>
            </div>

            <div className="mb-6 pb-6 border-b border-border/40">
              <div className="flex items-baseline gap-1.5 mb-2">
                <span className="text-5xl font-extrabold text-foreground tracking-tight">
                  <bdi>{fidelityOffer.priceMain}</bdi>
                </span>
                <span className="text-lg font-bold text-muted-foreground">
                  {fidelityOffer.priceUnit}
                </span>
                <span className="text-sm text-muted-foreground">{fidelityOffer.priceSuffix}</span>
              </div>
              <p className="text-xs text-muted-foreground">{fidelityOffer.priceNote}</p>
            </div>

            <ul className="space-y-3 mb-8 flex-1">
              {fidelityOffer.features.map((feature) => (
                <li key={feature} className="flex items-start gap-2.5 text-sm text-foreground">
                  <div className="rounded-full p-0.5 mt-0.5 shrink-0 bg-primary/10 text-primary">
                    <Check className="h-3.5 w-3.5" />
                  </div>
                  <span className="leading-relaxed">{feature}</span>
                </li>
              ))}
            </ul>

            <Button
              size="lg"
              variant="outline"
              className="w-full rounded-xl border-border/60 hover:border-primary/30 hover:bg-primary/5"
              asChild
            >
              <a
                href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
                  fidelityOffer.mailSubject
                )}`}
              >
                {fidelityOffer.cta}
              </a>
            </Button>
          </motion.div>

          {/* Marketing campaigns — the paid service */}
          <motion.div
            custom={2}
            variants={cardVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="relative flex flex-col rounded-2xl p-7 lg:p-8 border-2 border-primary bg-gradient-to-br from-primary/[0.06] to-accent/[0.04] shadow-xl shadow-primary/15 transition-all hover:shadow-2xl hover:shadow-primary/20"
          >
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <div className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-4 py-1 text-xs font-bold shadow-md shadow-primary/30">
                <Sparkles className="h-3 w-3" />
                Le seul service payant
              </div>
            </div>

            <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-sm shadow-primary/25">
              <Send className="h-6 w-6" />
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-bold text-foreground mb-1">{campaignOffer.name}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed min-h-[2.5rem]">
                {campaignOffer.description}
              </p>
            </div>

            <div className="mb-6 pb-6 border-b border-border/40">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                {campaignOffer.priceLeadIn}
              </p>
              <div className="flex items-baseline gap-1.5 mb-2">
                <span className="text-5xl font-extrabold text-foreground tracking-tight">
                  <bdi>{campaignOffer.priceMain}</bdi>
                </span>
                <span className="text-lg font-bold text-muted-foreground">
                  {campaignOffer.priceUnit}
                </span>
                <span className="text-sm text-muted-foreground">{campaignOffer.priceSuffix}</span>
              </div>
              <p className="text-xs text-muted-foreground mb-4">{campaignOffer.priceNote}</p>
              <ul className="space-y-1.5">
                {campaignOffer.packs.map((pack) => (
                  <li
                    key={pack.sms}
                    className="flex items-baseline justify-between gap-3 rounded-lg bg-primary/[0.06] border border-primary/10 px-3 py-2 text-sm"
                  >
                    <span className="font-semibold text-foreground">
                      Pack <bdi>{frNumber.format(pack.sms)}</bdi> SMS
                      <span className="ms-2 text-xs font-normal text-muted-foreground whitespace-nowrap">
                        <bdi>{pack.unitDh}</bdi> DH / SMS
                      </span>
                    </span>
                    <span className="font-bold text-primary whitespace-nowrap">
                      <bdi>{frNumber.format(pack.priceDh)}</bdi> DH
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <ul className="space-y-3 mb-8 flex-1">
              {campaignOffer.features.map((feature) => (
                <li key={feature} className="flex items-start gap-2.5 text-sm text-foreground">
                  <div className="rounded-full p-0.5 mt-0.5 shrink-0 bg-primary text-primary-foreground">
                    <Check className="h-3.5 w-3.5" />
                  </div>
                  <span className="leading-relaxed">{feature}</span>
                </li>
              ))}
            </ul>

            <Button
              size="lg"
              className="w-full rounded-xl shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30"
              asChild
            >
              <a
                href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
                  campaignOffer.mailSubject
                )}`}
              >
                {campaignOffer.cta}
              </a>
            </Button>
          </motion.div>
        </div>

        {/* Medina Ads ecosystem cross-promo */}
        <motion.a
          href="https://medina-ads.ma"
          target="_blank"
          rel="noopener noreferrer"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.35 }}
          className="group relative block mt-12 overflow-hidden rounded-2xl border border-primary/30 bg-gradient-to-r from-primary/[0.08] via-accent/[0.05] to-primary/[0.08] p-6 sm:p-7 transition-all hover:border-primary/50 hover:shadow-lg hover:shadow-primary/10"
        >
          <motion.div
            animate={{ x: ["0%", "100%"], opacity: [0, 0.3, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
            className="absolute inset-y-0 -left-1/3 w-1/3 bg-gradient-to-r from-transparent via-primary/10 to-transparent pointer-events-none"
          />
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
            <div className="rounded-xl bg-primary/15 p-3 shrink-0">
              <Wifi className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1">
              <p className="text-xs uppercase tracking-wider text-primary font-bold mb-1">
                L&apos;écosystème Medina
              </p>
              <h3 className="text-lg sm:text-xl font-bold text-foreground mb-1">
                Envie d&apos;aller plus loin ? Diffusez vos publicités sur les écrans
                du réseau Medina Ads.
              </h3>
              <p className="text-sm text-muted-foreground">
                Publicité locale pour attirer de nouveaux clients, fidélité pour les
                faire revenir : les deux se renforcent.
              </p>
            </div>
            <div className="inline-flex items-center gap-2 text-sm font-bold text-primary group-hover:gap-3 transition-all shrink-0">
              Voir Medina Ads
              <ArrowUpRight className="h-4 w-4 group-hover:scale-110 transition-transform" />
            </div>
          </div>
        </motion.a>

        <motion.p
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center text-xs text-muted-foreground mt-10"
        >
          Sans engagement · Désinscription possible à tout moment · TVA non incluse
          sur les packs SMS · Au-delà de <bdi>2 500</bdi> SMS, contactez-nous pour un
          pack sur mesure : {CONTACT_EMAIL}
        </motion.p>
      </div>
    </section>
  );
};
